import React, { useEffect, useMemo, useState, useCallback } from 'react';
import socket from '../../socket';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Collapse,
  IconButton,
  Avatar,
  TextField,
} from '@mui/material';
import { keyframes } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchSpaBookings, updateSpaBookingStatus, cancelSpaBooking, fetchSpaList } from '../../api/spaApi';
import BriefOrder from './BriefOrder';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';

const STATUS_FLOW = [
  'pending',
  'confirmed',
  'completed',
];

const STATUS_META = {
  pending: { label: 'Awaiting confirmation', color: 'warning' },
  confirmed: { label: 'Confirmed', color: 'info' },
  completed: { label: 'Completed', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'error' },
};

const STATUS_TRANSITIONS = {
  pending: { next: 'confirmed', label: 'Confirm booking' },
  confirmed: { next: 'completed', label: 'Mark as Completed' },
  completed: null,
};

const GOLD = '#C8A86B';
const glowPulse = keyframes`
  0% { box-shadow: 0 0 0px rgba(200,168,107,0.0); }
  50% { box-shadow: 0 0 18px rgba(200,168,107,0.6); }
  100% { box-shadow: 0 0 0px rgba(200,168,107,0.0); }
`;
const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const shimmerBorder = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Awaiting" },
  { key: "confirmed", label: "Confirmed" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const formatMoney = (value) => {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
    }).format(n);
  } catch (err) {
    return `${n.toFixed(2)}`;
  }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

function InfoItem({ label, value, secondary }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
      {secondary ? (
        <Typography variant="caption" color="text.secondary">
          {secondary}
        </Typography>
      ) : null}
    </Box>
  );
}

function StatusTimeline({ status }) {
  const activeIndex = useMemo(() => {
    const idx = STATUS_FLOW.indexOf(String(status));
    return idx >= 0 ? idx : 0;
  }, [status]);

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {STATUS_FLOW.map((key, idx) => {
          const completed = idx < activeIndex;
          const current = idx === activeIndex;
          return (
            <React.Fragment key={key}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  backgroundColor: completed || current ? '#C8A86B' : '#E5E7EB',
                  color: completed || current ? 'white' : '#6B7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {idx + 1}
              </Box>
              {idx !== STATUS_FLOW.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    backgroundColor: idx < activeIndex ? '#C8A86B' : '#E5E7EB',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
        {STATUS_FLOW.map((key) => (
          <Typography key={key} variant="caption" color="text.secondary">
            {STATUS_META[key]?.label || key}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

const CANCELLATION_REASONS = ['Guest request', 'Not available', 'Spa closed', 'Other'];

export default function SpaOrdersPage({ propertyId }) {
  const [outlets, setOutlets] = useState([]);
  const [activeOutletId, setActiveOutletId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});
  const [expanded, setExpanded] = useState({});
  const [newHighlights, setNewHighlights] = useState({}); // { [id]: true } transient glow
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [availableTime, setAvailableTime] = useState('');
  const reasonInputRef = React.useRef(null);

  const toggleExpanded = useCallback((id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  useEffect(() => {
    if (!propertyId) return;
    fetchSpaList(propertyId).then(data => {
      const formatted = (data || []).map(s => ({ id: s._id, name: s.name, hours: "10:00 – 22:00", tagline: s.subtitle, thumbUrl: s.coverImage?.url || "/spa-thumb.jpg" }));
      setOutlets(formatted);
      if (formatted.length > 0) {
        setActiveOutletId(formatted[0].id);
      }
    }).catch(err => {
      console.error("Failed to fetch spa list", err);
    });
  }, [propertyId]);

  const load = useCallback(async () => {
    if (!activeOutletId) return;
    setLoading(true);
    try {
      const params = { propertyId, spaId: activeOutletId };
      if (filter && filter !== 'all') params.status = filter;
      const data = await fetchSpaBookings(params);
      setRows(data);
      setError(null);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Failed to load spa bookings");
    } finally {
      setLoading(false);
    }
  }, [activeOutletId, propertyId, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!propertyId || !activeOutletId) return;

    const dbg = (...args) => {
      if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[SpaOrders]', ...args);
      }
    };

    socket.on('connect_error', (err) => dbg('connect_error', err?.message || err));
    socket.on('error', (err) => dbg('error', err?.message || err));
    socket.on('disconnect', (reason) => dbg('disconnect', reason));
    socket.io?.on?.('reconnect_attempt', (n) => dbg('reconnect_attempt', n));
    socket.io?.on?.('reconnect', (n) => dbg('reconnect', n));
    socket.io?.on?.('reconnect_error', (err) => dbg('reconnect_error', err?.message || err));

    socket.on('connect', () => {
      dbg('connected', { sid: socket.id, propertyId, activeOutletId });
      socket.emit('joinSpaBookingsRoom', propertyId);
      dbg('joined room', `spaBookings_${propertyId}`);
    });
    socket.on('room:joined', (info) => dbg('room:joined', info));

    // Upsert helper: only accept bookings belonging to the currently selected spa outlet
    const upsertIfActive = (incoming) => {
      const b = incoming?.booking || incoming; // supports both { booking } and plain doc
      if (!b) { dbg('skip: no payload'); return; }
      const rawSpaId = b?.spaId ?? b?.outletId ?? b?.spa?.id ?? b?.spa?._id ?? b?.serviceId;
      const spaId = (rawSpaId && (rawSpaId._id || rawSpaId)) || rawSpaId; // populated or raw
      if (!spaId) { dbg('skip: no spaId in payload', b); return; }
      if (String(spaId) !== String(activeOutletId)) {
        dbg('skip: different outlet', { payloadSpaId: String(spaId), activeOutletId: String(activeOutletId), id: String(b?._id || '') });
        return;
      }
      dbg('upsert', { id: String(b?._id || ''), status: b?.status, spaId: String(spaId) });
      setRows((prev) => {
        const idx = prev.findIndex((x) => String(x._id) === String(b._id));
        if (idx === -1) {
          dbg('insert at top', b);
          setNewHighlights((prevFlags) => ({ ...prevFlags, [String(b._id)]: true }));
          // auto-clear highlight after 4.5s
          setTimeout(() => {
            setNewHighlights((prevFlags) => {
              const copy = { ...prevFlags };
              delete copy[String(b._id)];
              return copy;
            });
          }, 4500);
          return [b, ...prev];
        }
        const copy = [...prev];
        copy[idx] = { ...prev[idx], ...b };
        dbg('merge at index', idx, copy[idx]);
        return copy;
      });
    };

    // Namespaced events
    socket.on('spa:booking:new', (payload) => { dbg('evt spa:booking:new', payload); upsertIfActive(payload); });

    socket.on('spa:booking:status', ({ bookingId, status, booking }) => {
      dbg('evt spa:booking:status', { bookingId, status, booking });
      if (booking) return upsertIfActive({ booking });
      setRows((prev) => prev.map((x) => (String(x._id) === String(bookingId) ? { ...x, status } : x)));
    });

    socket.on('spa:booking:update', ({ bookingId, change, booking }) => {
      dbg('evt spa:booking:update', { bookingId, change, booking });
      if (booking) return upsertIfActive({ booking });
      setRows((prev) => prev.map((x) => (String(x._id) === String(bookingId) ? { ...x, ...(booking || {}) } : x)));
    });

    // Legacy events compatibility
    socket.on('spaBooking:new', (doc) => { dbg('evt spaBooking:new (legacy)', doc); upsertIfActive(doc); });
    socket.on('spaBooking:updated', (doc) => { dbg('evt spaBooking:updated (legacy)', doc); upsertIfActive(doc); });
    socket.on('spaBookingStatusUpdate', ({ bookingId, status, booking }) => {
      dbg('evt spaBookingStatusUpdate (legacy)', { bookingId, status, booking });
      if (booking) return upsertIfActive(booking);
      setRows((prev) => prev.map((x) => (String(x._id) === String(bookingId) ? { ...x, status } : x)));
    });

    return () => {
      socket.off('connect');
      socket.off('connect_error');
      socket.off('error');
      socket.off('disconnect');
      socket.io?.off?.('reconnect_attempt');
      socket.io?.off?.('reconnect');
      socket.io?.off?.('reconnect_error');
      socket.off('spa:booking:new');
      socket.off('spa:booking:status');
      socket.off('spa:booking:update');
      socket.off('spaBooking:new');
      socket.off('spaBooking:updated');
      socket.off('spaBookingStatusUpdate');
      // keep connection alive; it's component-local but safe to keep while page active
    };
  }, [propertyId, activeOutletId]);

  const visibleBookings = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter(r => r.status === filter);
  }, [rows, filter]);

  const handleRequestCancel = useCallback((booking) => {
    setCancelTarget(booking);
    setCancellationReason('');
    setAvailableTime('');
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    const booking = cancelTarget;
    setUpdating(prev => ({ ...prev, [booking._id]: true }));
    try {
      let finalReason = cancellationReason;
      if (cancellationReason === 'Not available' && availableTime) {
        finalReason = `Not available, but we will be available on ${availableTime}`;
      }
      const res = await cancelSpaBooking(booking._id, finalReason);
      setRows(prev => prev.map(x => (x._id === booking._id ? res : x)));
    } catch (err) {
      console.error('Failed to cancel spa booking', err);
    } finally {
      setUpdating(prev => {
        const newState = { ...prev };
        delete newState[booking._id];
        return newState;
      });
      setCancelTarget(null);
      setCancellationReason('');
      setAvailableTime('');
    }
  }, [cancelTarget, cancellationReason, availableTime]);

  const advance = async (booking, nextStatus) => {
    const id = booking._id;
    setUpdating(prev => ({ ...prev, [id]: true }));
    try {
      await updateSpaBookingStatus(id, nextStatus);
      setRows(prev => prev.map(x => (x._id === id ? { ...x, status: nextStatus } : x)));

      // Notify guest
      try {
        const updatedBooking = { ...booking, status: nextStatus };
        fetch('/api/concierge-messages/spa-booking-update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ booking: updatedBooking, status: nextStatus }),
        });
      } catch (e) {
        console.error("Failed to send spa booking update to guest", e);
      }

    } catch (err) {
      console.error('Failed to advance spa booking status', err);
    } finally {
      setUpdating(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    }
  };

  return (
    <Box sx={{ pt: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Spa Bookings
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filter}
            onChange={(_e, val) => {
              if (val !== null) setFilter(val);
            }}
          >
            {FILTERS.map(f => (
              <ToggleButton key={f.key} value={f.key}>
                {f.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button variant="outlined" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {!activeOutletId ? (
        <Typography color="text.secondary">Select a spa to view bookings.</Typography>
      ) : loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading bookings…</Typography>
        </Box>
      ) : error ? (
        <Typography color="error.main">{error}</Typography>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No bookings recorded yet.</Typography>
      ) : visibleBookings.length === 0 ? (
        <Typography color="text.secondary">No bookings found for this filter.</Typography>
      ) : (
        <Stack spacing={2}>
          {visibleBookings.map((booking) => {
            const statusKey = booking?.status || 'pending';
            const meta = STATUS_META[statusKey] || { label: statusKey, color: 'default' };
            const transition = STATUS_TRANSITIONS[statusKey];
            const isUpdating = !!updating[booking._id];
            const isConfirmAction = transition?.next === 'confirmed';

            const isNew = !!newHighlights[String(booking._id)];
            return (
              <Paper
                key={booking._id}
                elevation={isNew ? 3 : 1}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: isNew ? `1px solid ${GOLD}` : '1px solid rgba(0,0,0,0.06)',
                  animation: isNew ? `${glowPulse} 1600ms ease-in-out 0s 3` : 'none',
                  transition: 'box-shadow 240ms ease, border-color 240ms ease',
                  position: 'relative',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Stack direction="column" spacing={0.25}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        #{String(booking?._id || '').slice(-6)}
                      </Typography>
                      {isNew && (
                        <Chip
                          label="New"
                          color="success"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.75rem',
                            opacity: 0,
                            animation: 'fadeInOut 4.5s ease forwards',
                            '@keyframes fadeInOut': {
                              '0%': { opacity: 0 },
                              '10%': { opacity: 1 },
                              '90%': { opacity: 1 },
                              '100%': { opacity: 0 },
                            },
                          }}
                        />
                      )}
                    </Stack>
                    {booking?.roomNumber ? (
                      <Typography variant="caption" color="text.secondary">
                        Room {booking.roomNumber}
                      </Typography>
                    ) : null}
                  </Stack>
                  <Chip
                    label={meta.label}
                    color={meta.color}
                    size="small"
                    sx={
                      (String(statusKey) === 'pending')
                        ? {
                            // Gradient shimmer for "Awaiting confirmation"
                            color: '#FFFFFF',
                            border: 'none',
                            backgroundImage: 'linear-gradient(90deg, #d97706 0%, #f59e0b 50%, #d97706 100%)',
                            backgroundSize: '300% 100%',
                            backgroundRepeat: 'no-repeat',
                            animation: `${shimmer} 2.2s linear infinite`,
                            willChange: 'background-position',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.04) inset',
                          }
                        : undefined
                    }
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <InfoItem label="Guest" value={booking?.guestName || '—'} />
                  <InfoItem label="Total" value={formatMoney(booking?.total)} />
                  <InfoItem label="Created" value={formatDateTime(booking?.createdAt)} />
                </Box>

                <StatusTimeline status={statusKey} />

                {Array.isArray(booking?.treatments) && booking.treatments.length > 0 ? (
                  <Box sx={{ mt: 1.5, mb: 1 }}>
                    <Box
                      onClick={() => toggleExpanded(booking._id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        Treatments ({booking.treatments.length})
                      </Typography>
                      <IconButton size="small" disableRipple>
                        <ExpandMoreIcon
                          fontSize="small"
                          style={{
                            transition: 'transform 200ms',
                            transform: expanded[booking._id] ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      </IconButton>
                    </Box>

                    {!expanded[booking._id] && (
                      <BriefOrder
                        treatments={booking.treatments}
                        booking={booking}
                        fallbackTime={
                          booking.preferredTime ||
                          booking.time ||
                          booking.startTime ||
                          booking.scheduledAt ||
                          booking.preferredDate
                        }
                        fallbackQty={booking.qty || booking.quantity}
                      />
                    )}
                    <Collapse in={!!expanded[booking._id]} timeout="auto" unmountOnExit>
                      <Stack spacing={1} sx={{ mt: 0.5 }}>
                        {booking.treatments.map((item, idx) => (
                          <Box key={(item.massageId || item._id || item.name || 'item') + String(idx)}>
                            <Stack direction="row" alignItems="center" spacing={1.25}>
                              <Avatar src={item.image} variant="rounded" sx={{ width: 36, height: 36 }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                  {item?.name || '—'}
                                </Typography>
                                {(() => {
                                  const durationRaw = item?.durationLabel ?? item?.duration ?? item?.minutes ?? item?.durationMinutes ?? '';
                                  const n = Number(durationRaw);
                                  const duration = !Number.isNaN(n) && String(durationRaw).trim() !== '' ? `${n} mins` : String(durationRaw || '');
                                  const qty = Number(item?.qty ?? item?.quantity ?? item?.count ?? booking?.qty ?? booking?.quantity ?? 1);

                                  // Date label: Today/Tomorrow or formatted date, prefer metadata label if given and no relative match
                                  let dateLabel = '';
                                  if (booking?.preferredDate) {
                                    const d = new Date(booking.preferredDate);
                                    const now = new Date();
                                    const dayDiff = Math.floor((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / (1000*60*60*24));
                                    if (dayDiff === 0) dateLabel = 'Today';
                                    else if (dayDiff === 1) dateLabel = 'Tomorrow';
                                    else dateLabel = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
                                  } else if (booking?.metadata?.preferredDateLabel) {
                                    dateLabel = booking.metadata.preferredDateLabel;
                                  }

                                  // Time label: prefer booking.preferredTime then item-specific
                                  const rawTime = item?.timeLabel || item?.time || item?.startTime || item?.scheduledAt || booking?.preferredTime || booking?.time || booking?.startTime || booking?.scheduledAt || '';
                                  let timeLabel = '';
                                  if (rawTime) {
                                    const t = new Date(`1970-01-01T${rawTime}`);
                                    timeLabel = !Number.isNaN(t.getTime()) ? t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }) : String(rawTime);
                                  }

                                  const parts = [];
                                  if (dateLabel) parts.push(dateLabel);
                                  if (timeLabel) parts.push(timeLabel);
                                  if (duration) parts.push(duration);
                                  parts.push(`×${qty}`);

                                  return (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {parts.join(' • ')}
                                    </Typography>
                                  );
                                })()}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {formatMoney((Number(item?.price) || 0) * (Number(item?.qty ?? booking?.qty ?? 1)))}
                              </Typography>
                            </Stack>
                            {idx !== booking.treatments.length - 1 ? (
                              <Divider light sx={{ mt: 1, mb: 0.5 }} />
                            ) : null}
                          </Box>
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                ) : null}

                <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.25 }}>
                  {transition && statusKey !== 'completed' && statusKey !== 'cancelled' ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      disabled={isUpdating}
                      onClick={() => advance(booking, transition.next)}
                      sx={{
                        borderRadius: 2,
                        px: 1.75,
                        py: 0.6,
                        textTransform: 'none',
                        fontWeight: 500,
                        backgroundColor: '#FFFFFF',
                        color: '#111827',
                        ...(isConfirmAction
                          ? {
                              // Shimmer border ONLY for Confirm action
                              border: '2px solid transparent',
                              backgroundImage:
                                'linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(90deg, #c8a86b 0%, #e0c98d 50%, #c8a86b 100%)',
                              backgroundOrigin: 'border-box',
                              backgroundClip: 'padding-box, border-box',
                              animation: `${shimmerBorder} 2s linear infinite`,
                              backgroundSize: '200% 100%',
                              '&:hover': {
                                backgroundImage:
                                  'linear-gradient(#f9fafb, #f9fafb), linear-gradient(90deg, #c8a86b 0%, #e0c98d 50%, #c8a86b 100%)',
                              },
                            }
                          : {
                              // Neutral outlined for "Completed" (no shimmer)
                              border: '1px solid #E5E7EB',
                              '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB' },
                            }),
                      }}
                    >
                      {transition.label.replace('Mark as ', '')}
                    </Button>
                  ) : null}
                  {(statusKey !== 'cancelled' && statusKey !== 'completed') ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={isUpdating}
                      onClick={() => handleRequestCancel(booking)}
                      sx={{
                        borderRadius: 2, // 8px
                        px: 1.75,
                        py: 0.6,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: '#FCA5A5',
                        backgroundColor: '#FEF2F2',
                        color: '#B91C1C',
                        '&:hover': { borderColor: '#EF4444', backgroundColor: '#FEE2E2' },
                      }}
                    >
                      Cancel
                    </Button>
                  ) : null}
                </Box>

                {booking?.note ? (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: 'rgba(200,168,107,0.08)',
                      borderRadius: 1.5,
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Guest Note
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {booking.note}
                    </Typography>
                  </Box>
                ) : null}

                {statusKey === 'cancelled' && booking.cancellationReason && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: 'rgba(220, 38, 38, 0.05)',
                      borderRadius: 1.5,
                      border: '1px solid rgba(220, 38, 38, 0.1)',
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} color="error.dark">
                      Cancellation Reason
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      {booking.cancellationReason}
                    </Typography>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}
      <ConfirmDialog
        open={!!cancelTarget}
        title="Cancel this booking?"
        description={
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              This will mark the spa booking as cancelled. Guests will no longer see it as active.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              {CANCELLATION_REASONS.map((reason) => (
                <Chip
                  key={reason}
                  label={reason}
                  onClick={() => {
                    if (reason === 'Other') {
                      setCancellationReason('');
                      setTimeout(() => reasonInputRef.current?.focus(), 100);
                    } else {
                      setCancellationReason(reason);
                    }
                  }}
                  color={CANCELLATION_REASONS.includes(cancellationReason) && cancellationReason === reason ? 'primary' : 'default'}
                  variant={CANCELLATION_REASONS.includes(cancellationReason) && cancellationReason === reason ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
            <TextField
              inputRef={reasonInputRef}
              autoFocus
              margin="dense"
              id="cancellation-reason"
              label="Reason for cancellation"
              type="text"
              fullWidth
              variant="outlined"
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              sx={{ mt: 2 }}
            />
            {cancellationReason === 'Not available' && (
              <TextField
                autoFocus
                margin="dense"
                id="available-time"
                label="Available Time"
                placeholder="e.g., tomorrow at 2pm"
                type="text"
                fullWidth
                variant="outlined"
                value={availableTime}
                onChange={(e) => setAvailableTime(e.target.value)}
                sx={{ mt: 2 }}
              />
            )}
          </Box>
        }
        confirmLabel="Yes, cancel it"
        cancelLabel="No, keep it"
        onClose={() => {
          setCancelTarget(null);
          setCancellationReason('');
          setAvailableTime('');
        }}
        onCancel={() => {
          setCancelTarget(null);
          setCancellationReason('');
          setAvailableTime('');
        }}
        onConfirm={handleConfirmCancel}
        confirmDisabled={!cancellationReason.trim() || (cancellationReason === 'Not available' && !availableTime.trim())}
      />
    </Box>
  );
}